/**
 * Desenho do escudo padrão de time.
 *
 * As regras — formatos, padrões, cores e como a escolha é gravada — moram em
 * `@mpc/core` (`escudo.ts`), para a API gravar exatamente o que esta tela
 * desenha. Aqui só se desenha: recebe um `EscudoPadrao` (o escolhido pelo
 * capitão) ou, sem ele, usa o que o nome do time sorteia.
 */

import { useId } from 'react';
import { escudoDoNome, lerEscudo, type EscudoPadrao, type FormatoDeEscudo } from '@mpc/core';

const CAMINHO: Record<FormatoDeEscudo, string> = {
  // Clássico, de ponta embaixo.
  classico: 'M10 8 H90 V55 C90 84 71 101 50 112 C29 101 10 84 10 55 Z',
  // Topo em ponta, como os escudos ingleses.
  ingles: 'M50 4 L90 16 V58 C90 86 71 103 50 114 C29 103 10 86 10 58 V16 Z',
  // Cantos de cima retos, base arredondada, estilo europeu moderno.
  moderno: 'M14 8 H86 Q92 8 92 14 V68 Q92 98 50 114 Q8 98 8 68 V14 Q8 8 14 8 Z',
  // Topo recortado em ondas, clube antigo.
  ondas: 'M10 16 Q30 4 50 16 Q70 4 90 16 V58 C90 86 71 103 50 114 C29 103 10 86 10 58 Z',
  // Redondo, de medalhão.
  medalhao: 'M50 12 A48 48 0 1 1 49.99 12 Z',
};

/**
 * As cores do escudo de um time, para tingir o card dele. Usa o escudo
 * gravado quando existe; sem ele, o do nome.
 */
export function coresDoEscudo(nome: string, crestUrl?: string | null) {
  const { fundo, detalhe } = lerEscudo(crestUrl) ?? escudoDoNome(nome);
  return { fundo, detalhe };
}

/** Preto ou branco, o que ler melhor sobre a cor. */
function contraste(hex: string) {
  const n = parseInt(hex.slice(1), 16);
  const r = (n >> 16) & 255;
  const g = (n >> 8) & 255;
  const b = n & 255;
  return 0.299 * r + 0.587 * g + 0.114 * b > 160 ? '#111111' : '#ffffff';
}

function siglaDe(nome: string, curto?: string) {
  const s = curto?.trim();
  if (s && s.length <= 3) return s.toUpperCase();
  const palavras = nome
    .replace(/[^\p{L}\p{N}\s]/gu, ' ')
    .split(/\s+/)
    .filter(Boolean)
    // "FC", "EC" e afins não identificam ninguém: quase todo time tem.
    .filter((p, _, todas) => todas.length === 1 || !/^(fc|ec|sc|ac|cf|clube|club|team|de|do|da)$/i.test(p));
  if (palavras.length === 0) return '?';
  if (palavras.length === 1) return palavras[0]!.slice(0, 2).toUpperCase();
  return (palavras[0]![0]! + palavras[1]![0]!).toUpperCase();
}

export function EscudoGerado({
  nome,
  sigla,
  escudo,
  className,
}: {
  nome: string;
  sigla?: string;
  /** O escudo escolhido. Sem ele, vale o que o nome sorteia. */
  escudo?: EscudoPadrao | null;
  className?: string;
}) {
  const id = useId().replace(/:/g, '');
  const { formato, padrao, fundo, detalhe } = escudo ?? escudoDoNome(nome);
  const caminho = CAMINHO[formato];
  const letras = siglaDe(nome, sigla);
  // Com padrão atrás das letras, uma placa escura garante a leitura.
  const comPlaca = padrao !== 'liso' && padrao !== 'faixa';
  const corDaLetra = comPlaca ? '#ffffff' : padrao === 'faixa' ? contraste(detalhe) : contraste(fundo);

  return (
    <svg viewBox="0 0 100 120" className={className} aria-hidden role="img">
      <defs>
        <clipPath id={`e${id}`}>
          <path d={caminho} />
        </clipPath>
        <linearGradient id={`b${id}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#fff" stopOpacity="0.22" />
          <stop offset="0.5" stopColor="#fff" stopOpacity="0" />
          <stop offset="1" stopColor="#000" stopOpacity="0.25" />
        </linearGradient>
      </defs>

      <g clipPath={`url(#e${id})`}>
        <rect width="100" height="120" fill={fundo} />
        {padrao === 'listras' &&
          [22, 50, 78].map((x) => <rect key={x} x={x - 7} y="0" width="14" height="120" fill={detalhe} />)}
        {padrao === 'faixa' && <rect x="0" y="44" width="100" height="26" fill={detalhe} />}
        {padrao === 'diagonal' && <path d="M-10 30 L30 -10 L110 70 L70 110 Z" fill={detalhe} />}
        {padrao === 'meio' && <rect x="50" y="0" width="50" height="120" fill={detalhe} />}
        {padrao === 'chevron' && <path d="M0 40 L50 72 L100 40 V62 L50 94 L0 62 Z" fill={detalhe} />}
        {padrao === 'quartos' && (
          <>
            <rect x="50" y="0" width="50" height="60" fill={detalhe} />
            <rect x="0" y="60" width="50" height="60" fill={detalhe} />
          </>
        )}
        <rect width="100" height="120" fill={`url(#b${id})`} />
      </g>

      {/* Borda dupla: a externa na cor de detalhe, a interna escura. */}
      <path d={caminho} fill="none" stroke={detalhe} strokeWidth="6" />
      <path d={caminho} fill="none" stroke="#000" strokeOpacity="0.35" strokeWidth="1.5" />

      {comPlaca && <rect x="20" y="44" width="60" height="30" rx="6" fill="#000" fillOpacity="0.55" />}
      <text
        x="50"
        y="60"
        textAnchor="middle"
        dominantBaseline="central"
        fontFamily="var(--font-display)"
        fontSize={letras.length > 2 ? 24 : 30}
        letterSpacing="0.5"
        fill={corDaLetra}
      >
        {letras}
      </text>
    </svg>
  );
}
